import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart3,
  Plus,
  Calendar,
  Users,
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  Edit,
  Trash2,
  Eye,
  TrendingUp,
  Award,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const Feedback180Main = () => {
  const navigate = useNavigate();
  const [ciclos, setCiclos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_ciclos: 0,
    ciclos_ativos: 0,
    ciclos_concluidos: 0,
    total_avaliacoes: 0
  });

  useEffect(() => {
    fetchCiclos();
  }, []);

  const fetchCiclos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/feedback-180/ciclos`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setCiclos(response.data);
      
      // Calcular stats
      const ativos = response.data.filter(c => c.status === 'ativo').length;
      const concluidos = response.data.filter(c => c.status === 'concluido').length;
      
      setStats({
        total_ciclos: response.data.length,
        ciclos_ativos: ativos,
        ciclos_concluidos: concluidos,
        total_avaliacoes: response.data.reduce((acc, c) => acc + (c.participants?.length || 0), 0)
      });
      
    } catch (error) {
      console.error('Erro ao buscar ciclos:', error);
      toast.error('Erro ao carregar ciclos de feedback');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCiclo = async (cicloId) => {
    if (!window.confirm('Tem certeza que deseja excluir este ciclo?')) {
      return;
    }

    try {
      await axios.delete(`${API}/api/feedback-180/ciclos/${cicloId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      toast.success('Ciclo excluído com sucesso!');
      fetchCiclos();
    } catch (error) {
      console.error('Erro ao excluir ciclo:', error);
      toast.error('Erro ao excluir ciclo');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      rascunho: { label: 'Rascunho', color: 'bg-gray-100 text-gray-700' },
      ativo: { label: 'Ativo', color: 'bg-green-100 text-green-700' },
      concluido: { label: 'Concluído', color: 'bg-blue-100 text-blue-700' },
      cancelado: { label: 'Cancelado', color: 'bg-red-100 text-red-700' }
    };
    
    const statusInfo = statusMap[status] || statusMap.rascunho;
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Feedback 180º</h1>
          <p className="text-gray-600 mt-1">
            Gestão de ciclos de feedback bidirecional (colaborador + gestor)
          </p>
        </div>
        
        <Button 
          onClick={() => navigate('/feedback/180/ciclo/novo')}
          className="bg-purple-600 hover:bg-purple-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Novo Ciclo 180º
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Ciclos</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_ciclos}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <Calendar className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Ciclos Ativos</p>
                <p className="text-2xl font-bold text-green-600">{stats.ciclos_ativos}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Concluídos</p>
                <p className="text-2xl font-bold text-blue-600">{stats.ciclos_concluidos}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Award className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Avaliações</p>
                <p className="text-2xl font-bold text-orange-600">{stats.total_avaliacoes}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <Users className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Ciclos List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>Ciclos de Feedback 180º</span>
          </CardTitle>
          <CardDescription>
            Gerencie todos os ciclos de avaliação bidirecional
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <p className="mt-2 text-gray-600">Carregando ciclos...</p>
            </div>
          ) : ciclos.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-lg mb-4">Nenhum ciclo criado ainda</p>
              <Button 
                onClick={() => navigate('/feedback/180/ciclo/novo')}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Criar Primeiro Ciclo
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {ciclos.map((ciclo) => (
                <div
                  key={ciclo.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {ciclo.nome}
                        </h3>
                        {getStatusBadge(ciclo.status)}
                      </div>
                      
                      {ciclo.descricao && (
                        <p className="text-sm text-gray-600 mb-3">{ciclo.descricao}</p>
                      )}
                      
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <div className="flex items-center space-x-1">
                          <Calendar className="h-4 w-4" />
                          <span>
                            {new Date(ciclo.periodo_inicio).toLocaleDateString()} - {' '}
                            {new Date(ciclo.periodo_fim).toLocaleDateString()}
                          </span>
                        </div>
                        
                        <div className="flex items-center space-x-1">
                          <Users className="h-4 w-4" />
                          <span>{ciclo.competencias_ids?.length || 0} competências</span>
                        </div>
                        
                        <div className="flex items-center space-x-1">
                          <TrendingUp className="h-4 w-4" />
                          <span>
                            Auto: {ciclo.peso_auto}% | Gestor: {ciclo.peso_gestor}%
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      {ciclo.status === 'ativo' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/feedback/180/dashboard/${ciclo.id}`)}
                        >
                          <BarChart3 className="h-4 w-4 mr-1" />
                          Dashboard
                        </Button>
                      )}
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/feedback/180/ciclo/${ciclo.id}`)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/feedback/180/ciclo/${ciclo.id}/editar`)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteCiclo(ciclo.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Guia Rápido */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-blue-600" />
            <span>Como Funciona o Feedback 180º</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-purple-50 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">
                  1
                </div>
                <h4 className="font-semibold text-gray-900">Criar Ciclo</h4>
              </div>
              <p className="text-sm text-gray-600">
                Configure o período, competências, público-alvo e pesos da avaliação
              </p>
            </div>
            
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                  2
                </div>
                <h4 className="font-semibold text-gray-900">Avaliar</h4>
              </div>
              <p className="text-sm text-gray-600">
                Colaboradores fazem autoavaliação e gestores avaliam seus subordinados
              </p>
            </div>
            
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                  3
                </div>
                <h4 className="font-semibold text-gray-900">Análise</h4>
              </div>
              <p className="text-sm text-gray-600">
                Compare percepções, identifique gaps e gere insights automáticos
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Feedback180Main;
